/**
 * Phase Б1 — Cookie / data-protection informational banner.
 *
 * Storica intentionally ships without tracking (no GA, no GTM, no
 * social pixels, no Google Fonts — see docs/legal/COOKIE_AUDIT.md).
 * The only third-party network call is the Telegram WebApp SDK, which
 * is TTDSG §25 Abs. 2 Nr. 2 strictly-necessary.
 *
 * That makes this banner an INFORMATIONAL NOTICE, not an opt-in
 * consent toggle. The user reads it once, clicks "Verstanden", and
 * we record the acknowledgement in audit_log for Art. 7 DSGVO
 * "ability to demonstrate" compliance.
 *
 * Versioning: when text or legal links change, bump COOKIE_NOTICE_VERSION
 * on the backend → the banner re-shows automatically because the
 * stored localStorage version != server version.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../api";


const STORAGE_KEY = "storica_cookie_notice_v1";


export function CookieBanner() {
  const { t, i18n } = useTranslation("cookie");
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Read previously-acknowledged version from storage.
  useEffect(() => {
    try {
      setAcceptedVersion(localStorage.getItem(STORAGE_KEY));
    } catch {
      // localStorage blocked (private mode etc) — we silently re-show
      // the banner each session; that's the safer GDPR default.
    }
  }, []);

  // Pull current server-side version; null on network error keeps the
  // banner hidden (do not block UX on a public endpoint outage).
  useEffect(() => {
    api
      .get<{ version: string }>("/public/cookie-consent/version")
      .then((r) => setServerVersion(r.data.version))
      .catch(() => setServerVersion(null));
  }, []);

  const needsBanner =
    serverVersion !== null && acceptedVersion !== serverVersion;

  if (!needsBanner) return null;

  const onAcknowledge = async () => {
    setSubmitting(true);
    try {
      await api.post("/public/cookie-consent", {
        accepted: true,
        locale: i18n.language?.slice(0, 5) || "en",
        notice_version: serverVersion,
        path:
          typeof window !== "undefined"
            ? window.location.pathname.slice(0, 200)
            : null,
      });
    } catch {
      // silent: fire-and-forget background tracking
    }
    try {
      localStorage.setItem(STORAGE_KEY, serverVersion as string);
    } catch {
      // silent: localStorage unavailable (private mode / quota)
    }
    setAcceptedVersion(serverVersion);
    setSubmitting(false);
  };

  return (
    <div
      role="dialog"
      data-testid="cookie-banner"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-0 inset-x-0 z-[60] px-3 pb-3 pointer-events-none"
    >
      <div
        className="max-w-[640px] mx-auto pointer-events-auto indigo-card-glass p-4 space-y-3"
        // Backdrop blur reduces visual weight without hiding content
        // behind it — important when the banner sits over goal pages.
      >
        <div id="cookie-banner-title" className="text-sm font-semibold text-fg-primary">
          {t("title", { defaultValue: "Хранение данных" })}
        </div>
        <div className="text-[12px] text-fg-secondary leading-relaxed">
          {t("body", {
            defaultValue:
              "Storica не использует трекеры, аналитику и сторонние шрифты. Хранятся только необходимые куки для авторизации. Подробнее — в политике конфиденциальности.",
          })}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={submitting}
            className="rounded-pill bg-accent hover:bg-accent/90 disabled:bg-accent/50 text-accent-on font-semibold px-4 py-2 text-sm transition-colors"
          >
            {submitting
              ? t("acknowledging", { defaultValue: "Записываю…" })
              : t("acknowledge", { defaultValue: "Понятно" })}
          </button>
          <Link
            to="/datenschutz"
            className="text-[12px] text-accent hover:text-accent/80 underline-offset-2 hover:underline"
          >
            {t("privacyLink", { defaultValue: "Политика конфиденциальности" })}
          </Link>
          <Link
            to="/impressum"
            className="text-[12px] text-fg-muted hover:text-fg-primary"
          >
            {t("impressumLink", { defaultValue: "Импрессум" })}
          </Link>
        </div>
      </div>
    </div>
  );
}
